import React from "react";
import { BadgeArt } from "../art";
import type { CategoryProgress, PublicHeldBadge } from "../types";
import { TierName } from "../TierName";
import { progressText, tierLine, tierLineText } from "./badgeFormat";
import "./badgeCollection.css";

export type BadgeTileSize = "tile" | "card";

const ART_PX: Record<BadgeTileSize, number> = { tile: 72, card: 104 };

/** A held badge on a shelf: the art (with its xN chip), the name, the tier and how many hold it. */
export const BadgeTile: React.FC<{
  badge: PublicHeldBadge;
  size?: BadgeTileSize;
  pinned?: boolean;
  onOpen?: (code: string) => void;
}> = ({ badge, size = "tile", pinned = false, onOpen }) => {
  const count = badge.count || 1;
  const line = tierLine({ tier: badge.tier, holders: badge.rarity?.holders ?? 0, members: badge.rarity?.members ?? 0, unique: badge.unique });
  const label = `${badge.name}. ${tierLineText(line)}${count > 1 ? ` Earned ${count} times.` : ""}${pinned ? " Pinned." : ""}`;
  return (
    <button
      type="button"
      className={`bcol-tile bcol-tile--${size}`}
      data-tier={badge.tier}
      data-code={badge.code}
      aria-label={label}
      onClick={() => onOpen?.(badge.code)}
    >
      <span className="bcol-tile__art">
        <BadgeArt
          category={badge.category}
          tier={badge.tier}
          number={badge.number}
          count={count}
          code={badge.code}
          name={badge.name}
          size={ART_PX[size]}
          variant="full"
        />
      </span>
      <span className="bcol-tile__name">{badge.name}</span>
      <span className="bcol-tile__meta">
        <span className="bcol-tile__tier">
          <TierName tier={badge.tier} />
          {count > 1 && <span className="bcol-tile__count">x{count}</span>}
        </span>
        {line.fact && <span className="bcol-tile__fact">{line.fact}</span>}
      </span>
      {pinned && <span className="bcol-tile__pin">Pinned</span>}
    </button>
  );
};

/** The next tier of a category the member is working toward: locked art and a progress bar. */
export const ProgressGhost: React.FC<{
  progress: CategoryProgress;
  size?: BadgeTileSize;
  onOpen?: (code: string) => void;
}> = ({ progress, size = "tile", onOpen }) => {
  const next = progress.next;
  if (!next) return null;
  const pct = Math.max(0, Math.min(100, Math.round(progress.pct || 0)));
  const text = progressText(progress);
  return (
    <button
      type="button"
      className={`bcol-tile bcol-tile--${size} bcol-tile--ghost`}
      data-code={next.code}
      aria-label={`Next: ${next.name}, ${text}`}
      onClick={() => onOpen?.(next.code)}
    >
      <span className="bcol-tile__art">
        <BadgeArt
          category={next.category}
          tier={next.tier}
          number={next.number}
          code={next.code}
          name={next.name}
          size={ART_PX[size]}
          variant="full"
          locked
        />
      </span>
      <span className="bcol-tile__name">{next.name}</span>
      <ProgressBar pct={pct} label={text} />
      <span className="bcol-tile__meta bcol-tile__meta--progress">{text}</span>
    </button>
  );
};

export const ProgressBar: React.FC<{ pct: number; label: string }> = ({ pct, label }) => (
  <span
    className="bcol-progress"
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={pct}
    aria-valuetext={label}
  >
    <span className="bcol-progress__fill" style={{ width: `${pct}%` }} />
  </span>
);
